import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  ArrowRightIcon, 
  CheckCircleIcon,
  PlayIcon
} from './Icons';
import { SIMULATION_SCENARIOS, APP_CONFIG } from '../data/content';

const HeroSection = ({ projectName, tagline }) => {
  const [selectedScenarioIndex, setSelectedScenarioIndex] = useState(0);
  const [activeStepIndex, setActiveStepIndex] = useState(null);
  const scenario = SIMULATION_SCENARIOS[selectedScenarioIndex];

  const getAgentColor = (agentType) => {
    switch (agentType) {
      case 'PLANNER_AGENT':
        return {
          bg: 'bg-violet-500/10 text-violet-600 dark:text-violet-400 border-violet-500/30',
          dot: 'bg-violet-500'
        };
      case 'RAG_AGENT':
        return {
          bg: 'bg-cyan-500/10 text-cyan-600 dark:text-cyan-400 border-cyan-500/30',
          dot: 'bg-cyan-500'
        };
      case 'TOOL_AGENT':
        return {
          bg: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/30',
          dot: 'bg-emerald-500'
        };
      case 'CODE_AGENT':
        return {
          bg: 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/30',
          dot: 'bg-amber-500'
        };
      case 'CRITIC_AGENT':
        return {
          bg: 'bg-rose-500/10 text-rose-600 dark:text-rose-400 border-rose-500/30',
          dot: 'bg-rose-500'
        };
      default:
        return {
          bg: 'bg-slate-500/10 text-slate-600 dark:text-slate-400 border-slate-500/30',
          dot: 'bg-slate-500'
        };
    }
  };

  return (
    <section className="relative pt-12 pb-20 md:pt-20 md:pb-32 max-w-7xl mx-auto px-6 sm:px-8 overflow-hidden">
      {/* Ambient Apple-style Atmospheric Glow */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none -z-10">
        <div className="absolute -top-32 left-1/4 w-[600px] h-[600px] bg-gradient-to-tr from-brand-600/15 via-violet-500/10 to-accent-500/10 rounded-full blur-3xl opacity-70 animate-pulse-slow" />
        <div className="absolute top-1/2 -right-32 w-[500px] h-[500px] bg-gradient-to-br from-accent-500/10 to-indigo-500/10 rounded-full blur-3xl opacity-60" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
        {/* Left Pitch Column */}
        <div className="lg:col-span-6 space-y-7 text-left">
          {/* Eyebrow Pill */}
          <motion.div 
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="inline-flex items-center gap-2.5 px-3.5 py-1.5 rounded-full border border-brand-500/30 bg-brand-500/10 dark:bg-brand-950/40 text-xs font-semibold text-brand-700 dark:text-brand-300 font-inter shadow-sm backdrop-blur-md"
          >
            <span className="w-2 h-2 rounded-full bg-brand-500 dark:bg-brand-400 animate-ping" />
            <span>{APP_CONFIG.engineName} &bull; Autonomous DAG Architecture</span>
          </motion.div>

          {/* Main Display Headline */}
          <motion.h1 
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.08 }}
            className="text-4xl sm:text-6xl lg:text-5xl xl:text-6xl font-extrabold tracking-tight font-outfit text-slate-900 dark:text-white leading-[1.08]"
          >
            Go Beyond Chatbots. <br />
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-brand-600 via-indigo-500 to-accent-500 dark:from-brand-400 dark:via-indigo-300 dark:to-accent-400">
              Orchestrate Autonomous Multi-Agent Action.
            </span>
          </motion.h1>

          {/* Detailed Subtitle */}
          <motion.p 
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.16 }}
            className="text-base sm:text-lg text-slate-600 dark:text-slate-300 leading-relaxed font-normal max-w-xl"
          >
            Coordinate specialized AI agents in structured, deterministic dependency graphs. Break down complex requests with <strong className="text-slate-900 dark:text-white font-semibold">Planner</strong>, retrieve grounded knowledge with <strong className="text-slate-900 dark:text-white font-semibold">Hybrid RAG</strong>, execute real SaaS transactions with <strong className="text-slate-900 dark:text-white font-semibold">Tool Agent</strong>, and verify quality with a mandatory <strong className="text-slate-900 dark:text-white font-semibold">Critic Gate</strong>.
          </motion.p>

          {/* Action CTAs */}
          <motion.div 
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.24 }}
            className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 pt-2"
          >
            <a 
              href={APP_CONFIG.studioUrl}
              className="px-6 py-3.5 rounded-xl font-semibold text-sm text-white bg-gradient-to-r from-brand-600 to-indigo-600 hover:from-brand-500 hover:to-indigo-500 shadow-lg shadow-brand-500/25 active:scale-[0.98] transition-all duration-150 flex items-center justify-center gap-2 border border-brand-400/30"
            >
              <span>Launch Cortex Studio</span>
              <ArrowRightIcon className="w-4 h-4" />
            </a>
            <a 
              href="#simulator"
              onClick={(e) => {
                e.preventDefault();
                const el = document.getElementById('simulator');
                if (el) el.scrollIntoView({ behavior: 'smooth' });
              }}
              className="px-6 py-3.5 rounded-xl font-semibold text-sm text-slate-800 dark:text-slate-200 glass-panel hover:bg-slate-100 dark:hover:bg-slate-800/80 active:scale-[0.98] transition-all duration-150 flex items-center justify-center gap-2 shadow-sm"
            >
              <PlayIcon className="w-4 h-4 text-brand-500" />
              <span>Explore Interactive DAG</span>
            </a>
          </motion.div>

          {/* Key Value Metric Badges */}
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.32 }}
            className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-6 border-t border-slate-200 dark:border-slate-800/80"
          >
            <div className="p-2.5 rounded-xl bg-slate-100/70 dark:bg-slate-900/60 border border-slate-200/70 dark:border-slate-800">
              <div className="text-xs font-bold text-slate-900 dark:text-white font-outfit">Sub-400ms</div>
              <div className="text-[11px] text-slate-500 dark:text-slate-400">Tool Execution</div>
            </div>
            <div className="p-2.5 rounded-xl bg-slate-100/70 dark:bg-slate-900/60 border border-slate-200/70 dark:border-slate-800">
              <div className="text-xs font-bold text-emerald-600 dark:text-emerald-400 font-outfit">100% Critic</div>
              <div className="text-[11px] text-slate-500 dark:text-slate-400">Gate Verification</div>
            </div>
            <div className="p-2.5 rounded-xl bg-slate-100/70 dark:bg-slate-900/60 border border-slate-200/70 dark:border-slate-800">
              <div className="text-xs font-bold text-slate-900 dark:text-white font-outfit">ThreadLocal</div>
              <div className="text-[11px] text-slate-500 dark:text-slate-400">Tenant Isolation</div>
            </div>
            <div className="p-2.5 rounded-xl bg-slate-100/70 dark:bg-slate-900/60 border border-slate-200/70 dark:border-slate-800">
              <div className="text-xs font-bold text-brand-600 dark:text-brand-400 font-outfit">12+ Connectors</div>
              <div className="text-[11px] text-slate-500 dark:text-slate-400">SaaS & Databases</div>
            </div>
          </motion.div>
        </div>

        {/* Right Interactive Simulator Column */}
        <div id="simulator" className="lg:col-span-6 relative">
          <div className="relative rounded-3xl glass-panel p-5 sm:p-6 shadow-2xl shadow-slate-900/10 dark:shadow-black/50 border border-slate-200/80 dark:border-white/10 specular-border">
            {/* Simulator Header & Traffic Dots */}
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200/80 dark:border-slate-800/80 pb-4 mb-4">
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-rose-500/80" />
                <span className="w-3 h-3 rounded-full bg-amber-500/80" />
                <span className="w-3 h-3 rounded-full bg-emerald-500/80" />
                <span className="text-xs font-semibold text-slate-700 dark:text-slate-300 ml-2 font-outfit">
                  DAG Orchestrator Trace
                </span>
              </div>
              <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-md text-[10px] font-mono bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-ping" />
                STATUS: READY
              </span>
            </div>

            {/* Scenario Picker Pills */}
            <div className="mb-4">
              <label className="text-[11px] font-bold text-slate-400 uppercase tracking-wider block mb-2">
                Select Enterprise Workflow Scenario:
              </label>
              <div className="flex flex-wrap gap-1.5">
                {SIMULATION_SCENARIOS.map((sc, idx) => (
                  <button
                    key={sc.id}
                    onClick={() => {
                      setSelectedScenarioIndex(idx);
                      setActiveStepIndex(null);
                    }}
                    className={`px-3 py-1.5 rounded-xl text-xs font-semibold transition-all duration-200 active:scale-95 ${
                      selectedScenarioIndex === idx
                        ? 'bg-brand-600 text-white shadow-md shadow-brand-500/20'
                        : 'bg-slate-100 dark:bg-slate-800/80 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700'
                    }`}
                  >
                    {sc.title}
                  </button>
                ))}
              </div>
            </div>

            {/* User Request Bubble */}
            <div className="p-3.5 rounded-2xl bg-slate-100/80 dark:bg-slate-800/70 border border-slate-200 dark:border-slate-700/60 mb-4">
              <div className="text-[10px] font-bold text-slate-400 dark:text-slate-400 uppercase tracking-wider mb-1 flex items-center justify-between">
                <span>Inbound Enterprise Request</span>
                <span className="font-mono text-[9px] text-slate-400">POST /api/orchestrator/plan</span>
              </div>
              <p className="text-xs text-slate-800 dark:text-slate-200 font-medium leading-relaxed">
                "{scenario.userQuery}"
              </p>
            </div>

            {/* Step-by-Step DAG Pipeline Visual */}
            <div className="space-y-2 mb-4 max-h-[300px] overflow-y-auto pr-1">
              <div className="text-[10px] font-bold text-slate-400 dark:text-slate-400 uppercase tracking-wider mb-1">
                Multi-Agent Execution Pipeline (Click step for details):
              </div>
              {scenario.steps.map((st, i) => {
                const color = getAgentColor(st.agent);
                const isSelected = activeStepIndex === i;
                return (
                  <div
                    key={st.step}
                    onClick={() => setActiveStepIndex(isSelected ? null : i)}
                    className={`p-2.5 rounded-xl border transition-all duration-150 cursor-pointer text-left ${
                      isSelected 
                        ? 'bg-brand-500/10 border-brand-500 dark:bg-brand-950/40 shadow-sm' 
                        : 'bg-white/80 dark:bg-slate-900/60 border-slate-200/80 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700'
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold bg-slate-200 dark:bg-slate-800 text-slate-700 dark:text-slate-300 shrink-0">
                          {st.step}
                        </span>
                        <span className={`px-2 py-0.5 rounded text-[10px] font-mono font-bold border shrink-0 ${color.bg}`}>
                          {st.agent}
                        </span>
                        <span className="text-xs text-slate-700 dark:text-slate-300 truncate font-medium">
                          {st.action}
                        </span>
                      </div>
                      <span className="text-[10px] font-mono text-slate-400 shrink-0">
                        {st.latency}
                      </span>
                    </div>

                    {/* Expandable Step Inspector */}
                    {isSelected && (
                      <motion.div 
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        className="mt-2.5 pt-2 border-t border-slate-200 dark:border-slate-800 text-[11px] font-mono text-slate-600 dark:text-slate-300 bg-slate-50 dark:bg-slate-950 p-2 rounded-lg"
                      >
                        <div className="text-emerald-600 dark:text-emerald-400 font-bold mb-1">
                          ✔ Verified & Bound: Step {st.step}
                        </div>
                        <div className="text-[10px] text-slate-500 dark:text-slate-400">
                          Agent Type: <span className="text-slate-800 dark:text-slate-200">{st.agent}</span>
                        </div>
                        <div className="text-[10px] text-slate-500 dark:text-slate-400">
                          Task Description: <span className="text-slate-800 dark:text-slate-200">{st.action}</span>
                        </div>
                        <div className="text-[10px] text-slate-500 dark:text-slate-400">
                          Validation Gate: <span className="text-emerald-500">MVEL Policy Compliant</span>
                        </div>
                      </motion.div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Final Executed Output */}
            <div className="p-3 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-950 dark:text-emerald-200">
              <div className="flex items-center gap-1.5 text-xs font-bold text-emerald-700 dark:text-emerald-400 mb-1">
                <CheckCircleIcon className="w-4 h-4" />
                <span>Critic Verified Output & Execution Complete</span>
              </div>
              <p className="text-xs text-slate-700 dark:text-slate-300 font-medium leading-relaxed">
                {scenario.outcome}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
